"use client";

import { ChevronDown } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/Popover";
import { cn } from "@/lib/utils";

import { navGlassDropdownClass, navPillLinkClass } from "./Nav.constants";

type NavDropdownMenuProps = {
  label: string;
  isActive: boolean;
  children: ReactNode;
  contentClassName?: string;
};

const HOVER_CLOSE_DELAY_MS = 120;

export const NavDropdownMenu = ({
  label,
  isActive,
  children,
  contentClassName,
}: NavDropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(
      () => setOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  };

  const keepOpen = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  return (
    <div onMouseEnter={keepOpen} onMouseLeave={scheduleClose}>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger
          nativeButton
          className={navPillLinkClass(isActive || open)}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          {label}
          <ChevronDown
            className={cn(
              "size-3.5 transition-transform",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          initialFocus={false}
          onMouseEnter={keepOpen}
          onMouseLeave={scheduleClose}
          className={cn(
            navGlassDropdownClass,
            "block h-auto w-auto min-w-52 gap-0 p-2 shadow-lg ring-0",
            contentClassName,
          )}
        >
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
};
